import DashboardLayout from "@/components/DashboardLayout";
import ProductFindImageWorkflow from "@/components/sales/catalogue/ProductFindImageWorkflow";
import { useAuth } from "@/_core/hooks/useAuth";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { downloadHrsRackConfigurationPdf } from "@/lib/hrsRackConfigurationPdf";
import {
  buildHrsConfiguratorPath,
  HRS_CATALOGUE_PATH,
  resolveHrsReturnPath,
} from "@shared/hrsConfiguratorNavigation";
import {
  checkHrsEquipmentFit,
  findHrsBase,
  findHrsFrame,
  HRS_BASE_OPTIONS,
  HRS_SXR_SOLID_BRACE_CLEARANCE_IN,
  classifyHrsEquipmentLookup,
  type HrsBaseSeries,
  type HrsEquipmentDimensions,
  type HrsFinish,
  type HrsSeries,
  mergeHrsEquipmentDimensions,
} from "@shared/hrsRackConfigurator";
import { isHrsTesterEmail } from "@shared/hrsTesterAccess";
import {
  isHrsRackSnapshot,
  type HrsRackConfigurationSnapshot,
} from "@shared/hrsRackConfigurationSnapshot";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  CircleDashed,
  Download,
  ImagePlus,
  Loader2,
  Maximize2,
  Ruler,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect } from "wouter";
import { toast } from "sonner";

export type EquipmentSlot = HrsEquipmentDimensions & {
  key: string;
  baseSeries?: HrsBaseSeries;
  name: string;
  spacingIn: number;
  source: "none" | "library" | "web" | "manual";
  confidence: "high" | "medium" | "low" | null;
  imageUrl: string | null;
  solidBrace: boolean;
};
type CustomBase = {
  series: "E1X" | "M3X2";
  widthIn: number;
  depthIn: number;
};
const E1X_CUSTOM_WIDTHS = [9.5, 15, 17, 19, 21, 23, 25];
const emptySlot = (key: string, spacingIn = 8): EquipmentSlot => ({
  key,
  name: "",
  widthIn: null,
  depthIn: null,
  heightIn: null,
  weightKg: null,
  spacingIn,
  source: "none",
  confidence: null,
  imageUrl: null,
  solidBrace: false,
});

function toInches(
  value: string | number | null | undefined,
  unit: string | null | undefined
) {
  const n = value == null || value === "" ? null : Number(value);
  if (n == null || !Number.isFinite(n)) return null;
  if (unit === "mm") return n / 25.4;
  if (unit === "cm") return n / 2.54;
  return n;
}
function toKg(
  value: string | number | null | undefined,
  unit: string | null | undefined
) {
  const n = value == null || value === "" ? null : Number(value);
  if (n == null || !Number.isFinite(n)) return null;
  return unit?.toLowerCase().includes("lb") ? n * 0.453592 : n;
}
function numberOrNull(value: string) {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeEquipmentSearch(value: string) {
  return value
    .normalize("NFKC")
    .toLowerCase()
    .replace(/\b(?:no|number)\b/g, "")
    .replace(/[^a-z0-9]+/g, "");
}
function money(value: number) {
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 0,
  }).format(value);
}
function FitIcon({
  status,
}: {
  status: ReturnType<typeof checkHrsEquipmentFit>["status"];
}) {
  if (status === "fits")
    return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "does_not_fit")
    return <AlertTriangle className="h-4 w-4 text-red-600" />;
  return <CircleDashed className="h-4 w-4 text-slate-500" />;
}
function fitClasses(status: ReturnType<typeof checkHrsEquipmentFit>["status"]) {
  if (status === "fits")
    return "border-emerald-200 bg-emerald-50 text-emerald-900";
  if (status === "does_not_fit") return "border-red-200 bg-red-50 text-red-900";
  if (status === "review") return "border-amber-200 bg-amber-50 text-amber-900";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function fitRecommendation(slot: EquipmentSlot, selectedSku: string) {
  const compatible = HRS_BASE_OPTIONS.filter(
    option =>
      (slot.widthIn == null || option.widthIn >= Math.round(slot.widthIn)) &&
      (slot.depthIn == null || option.depthIn >= Math.round(slot.depthIn))
  ).sort(
    (a, b) =>
      a.widthIn * a.depthIn - b.widthIn * b.depthIn || a.msrpUsd - b.msrpUsd
  );
  const recommended = compatible[0];
  if (recommended && recommended.sku !== selectedSku) {
    return `Recommended: use ${recommended.sku} (${recommended.widthIn}″ W × ${recommended.depthIn}″ D) with a matching frame, or request a custom HRS solution.`;
  }
  return "Recommended: select a taller opening or larger published shelf where available; otherwise request a custom HRS solution.";
}

export function wrapHrsEquipmentLabel(name: string, maxChars: number) {
  const limit = Math.max(6, Math.floor(maxChars));
  const words = name.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  const pushWord = (word: string) => {
    if (!current) {
      current = word;
      return;
    }
    if (`${current} ${word}`.length <= limit) {
      current = `${current} ${word}`;
      return;
    }
    lines.push(current);
    current = word;
  };
  for (const word of words) {
    if (word.length <= limit) {
      pushWord(word);
      continue;
    }
    if (current) {
      lines.push(current);
      current = "";
    }
    for (let offset = 0; offset < word.length; offset += limit) {
      const chunk = word.slice(offset, offset + limit);
      if (chunk.length === limit) lines.push(chunk);
      else current = chunk;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

export function JoinedRackElevation({
  series,
  finish,
  rackLevels,
  slots,
  baseSku,
  baseSkusBySlot,
  baseWidth,
  baseDepth,
  onSelectSlot,
  selectedSlotKey,
}: {
  series: HrsSeries;
  finish: HrsFinish;
  rackLevels: number[];
  slots: EquipmentSlot[];
  baseSku: string;
  baseSkusBySlot?: Record<string, string>;
  baseWidth: number;
  baseDepth: number;
  onSelectSlot: (key: string) => void;
  selectedSlotKey: string;
}) {
  const bayWidth = 146,
    pad = 38,
    inchPx = 7,
    shelfStepPx = 13;
  const rackHeights = rackLevels.map((count, rackIndex) =>
    Array.from(
      { length: count },
      (_, levelIndex) =>
        Math.max(
          18,
          levelIndex === count - 1
            ? (slots.find(
                slot => slot.key === `${rackIndex + 1}-${levelIndex + 1}`
              )?.heightIn ?? 6) *
                inchPx +
                24
            : (slots.find(
                slot => slot.key === `${rackIndex + 1}-${levelIndex + 1}`
              )?.spacingIn ?? 8) * inchPx
        ) + (levelIndex < count - 1 ? shelfStepPx : 0)
    ).reduce((sum, value) => sum + value, 0)
  );
  const width = pad * 2 + rackLevels.length * bayWidth,
    height = Math.max(340, Math.max(...rackHeights) + 115),
    bottom = height - 68;
  const ink = finish === "silver" ? "#6f777b" : "#141819",
    baseFill = finish === "silver" ? "#aeb4b7" : "#252b2e";
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-auto w-full"
      role="img"
      aria-label={`Joined ${series} rack elevation with ${rackLevels.length} racks`}
    >
      <line
        x1={pad - 9}
        y1={bottom + 17}
        x2={width - pad + 9}
        y2={bottom + 17}
        stroke={ink}
        strokeWidth="4"
      />
      {rackLevels.map((levelCount, rackIndex) => {
        const x = pad + rackIndex * bayWidth;
        let shelfY = bottom;
        const rows = [];
        let topShelfY = bottom;
        for (let levelIndex = 0; levelIndex < levelCount; levelIndex += 1) {
          const slot =
            slots.find(
              candidate =>
                candidate.key === `${rackIndex + 1}-${levelIndex + 1}`
            ) ?? emptySlot("missing");
          const openingPx = slot.spacingIn * inchPx;
          const isTopLevel = levelIndex === levelCount - 1;
          const braceAbove =
            series === "SXR" &&
            slots.find(
              candidate =>
                candidate.key === `${rackIndex + 1}-${levelIndex + 2}`
            )?.solidBrace;
          const usableHeightIn = braceAbove
            ? Math.max(0, slot.spacingIn - HRS_SXR_SOLID_BRACE_CLEARANCE_IN)
            : slot.spacingIn;
          const fit = checkHrsEquipmentFit({
            equipmentName: slot.name,
            dimensions: slot,
            base: { widthIn: baseWidth, depthIn: baseDepth },
            clearHeightIn: isTopLevel
              ? Number.POSITIVE_INFINITY
              : usableHeightIn,
            ventilationAllowanceIn: isTopLevel ? 0 : 2,
          });
          const equipmentHeight =
            slot.heightIn == null ? 0 : Math.max(2, slot.heightIn * inchPx);
          const shelfWidthPx = bayWidth - 14;
          const equipmentWidth =
            slot.widthIn == null
              ? shelfWidthPx * 0.72
              : Math.max(2, (slot.widthIn / baseWidth) * shelfWidthPx);
          const equipmentX = x + (bayWidth - equipmentWidth) / 2;
          const equipmentY = shelfY - 10 - equipmentHeight;
          const equipmentLabelLines = wrapHrsEquipmentLabel(
            slot.name,
            Math.max(10, Math.floor((equipmentWidth - 4) / 3.8))
          );
          const clipId = `equipment-${rackIndex}-${levelIndex}`;
          rows.push(
            <g
              key={levelIndex}
              role="button"
              tabIndex={0}
              aria-label={`Configure Rack ${rackIndex + 1}, Level ${levelIndex + 1}`}
              className="cursor-pointer outline-none focus-visible:[&>rect]:stroke-amber-500"
              onClick={() => onSelectSlot(slot.key)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectSlot(slot.key);
                }
              }}
            >
              <defs>
                <clipPath id={clipId}>
                  <rect
                    x={equipmentX}
                    y={equipmentY}
                    width={equipmentWidth}
                    height={equipmentHeight}
                  />
                </clipPath>
              </defs>
              <rect
                x={x + 3}
                y={shelfY - Math.max(openingPx, equipmentHeight + 20)}
                width={bayWidth - 6}
                height={Math.max(openingPx, equipmentHeight + 20)}
                fill="transparent"
                pointerEvents="all"
              />
              {selectedSlotKey === slot.key && (
                <rect
                  x={x + 1}
                  y={shelfY - Math.max(openingPx, equipmentHeight + 20) - 2}
                  width={bayWidth - 2}
                  height={Math.max(openingPx, equipmentHeight + 20) + 4}
                  rx="3"
                  fill="#f59e0b"
                  fillOpacity="0.08"
                  stroke="#d97706"
                  strokeWidth="2.5"
                />
              )}
              <rect
                x={x + 7}
                y={shelfY - 10}
                width={bayWidth - 14}
                height="10"
                fill={baseFill}
                stroke={ink}
                strokeWidth="2"
              />
              <text
                x={x + bayWidth / 2}
                y={shelfY - 2}
                textAnchor="middle"
                className="fill-white text-[7px] font-semibold"
              >
                {baseSkusBySlot?.[slot.key] ?? baseSku}
              </text>
              {series === "SXR" && slot.solidBrace && (
                <g aria-label="SXR Solid Brace Insert, one pair">
                  <rect
                    x={x + 11}
                    y={shelfY}
                    width={bayWidth - 22}
                    height="3"
                    rx="1"
                    fill="#b7791f"
                  />
                  <line
                    x1={x + 18}
                    y1={shelfY + 4}
                    x2={x + bayWidth / 2 - 3}
                    y2={shelfY + 8}
                    stroke="#b7791f"
                    strokeWidth="2"
                  />
                  <line
                    x1={x + bayWidth - 18}
                    y1={shelfY + 4}
                    x2={x + bayWidth / 2 + 3}
                    y2={shelfY + 8}
                    stroke="#b7791f"
                    strokeWidth="2"
                  />
                </g>
              )}
              {slot.name ? (
                <g>
                  <rect
                    x={equipmentX}
                    y={equipmentY}
                    width={equipmentWidth}
                    height={equipmentHeight}
                    fill={slot.imageUrl ? "#ffffff" : "#f4f4f1"}
                    stroke={fit.status === "does_not_fit" ? "#dc2626" : ink}
                    strokeWidth="1.5"
                  />
                  {slot.imageUrl && (
                    <image
                      href={slot.imageUrl}
                      x={equipmentX + 2}
                      y={equipmentY + 2}
                      width={equipmentWidth - 4}
                      height={Math.max(1, equipmentHeight - 4)}
                      preserveAspectRatio="xMidYMid meet"
                      clipPath={`url(#${clipId})`}
                    />
                  )}
                  <text
                    x={x + bayWidth / 2}
                    y={equipmentY - 3 - (equipmentLabelLines.length - 1) * 8}
                    textAnchor="middle"
                    className="fill-slate-800 text-[7px] font-semibold"
                    paintOrder="stroke"
                    stroke="#ffffff"
                    strokeWidth="2.5"
                  >
                    {equipmentLabelLines.map((line, lineIndex) => (
                      <tspan
                        key={`${slot.key}-label-${lineIndex}`}
                        x={x + bayWidth / 2}
                        dy={lineIndex === 0 ? 0 : 8}
                      >
                        {line}
                      </tspan>
                    ))}
                  </text>
                </g>
              ) : (
                <text
                  x={x + bayWidth / 2}
                  y={shelfY - 31}
                  textAnchor="middle"
                  className="fill-slate-400 text-[8px]"
                >
                  Equipment TBD
                </text>
              )}
              {levelIndex < levelCount - 1 && (
                <line
                  x1={x + 3}
                  y1={shelfY - 10}
                  x2={x + 3}
                  y2={shelfY - openingPx}
                  stroke="#8b9498"
                />
              )}
              {!isTopLevel && (
                <text
                  x={x - 8}
                  y={shelfY - openingPx / 2}
                  textAnchor="end"
                  className="fill-slate-500 text-[8px]"
                >
                  {slot.spacingIn}&quot;
                </text>
              )}
            </g>
          );
          topShelfY = shelfY;
          if (levelIndex < levelCount - 1) shelfY -= openingPx + shelfStepPx;
        }
        return (
          <g key={rackIndex}>
            {rows}
            <rect
              x={x - 4}
              y={topShelfY - 1}
              width="10"
              height={bottom + 11 - topShelfY}
              fill={ink}
            />
            <rect
              x={x + bayWidth - 6}
              y={topShelfY - 1}
              width="10"
              height={bottom + 11 - topShelfY}
              fill={ink}
            />
            <text
              x={x + bayWidth / 2}
              y={bottom + 31}
              textAnchor="middle"
              className="fill-slate-700 text-[10px] font-semibold"
            >
              Rack {rackIndex + 1}
            </text>
          </g>
        );
      })}
      <text
        x={width / 2}
        y={height - 18}
        textAnchor="middle"
        className="fill-slate-500 text-[9px]"
      >
        <tspan x={width / 2}>Joined {series} elevation</tspan>
        <tspan x={width / 2} dy="12">
          Nominal {baseWidth}&quot; W × {baseDepth}&quot; D shelves
        </tspan>
      </text>
    </svg>
  );
}

export default function HrsRackConfiguratorPage() {
  const navigationParams = new URLSearchParams(window.location.search);
  const proposalEditKey = navigationParams.get("proposalEditKey");
  const proposalRef = navigationParams.get("proposalRef")?.trim() || "Proposal";
  const proposalEditSource = useMemo<HrsRackConfigurationSnapshot | null>(() => {
    if (!proposalEditKey) return null;
    const key = `hrs-rack-proposal-edit-source:${proposalEditKey}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as unknown;
      return isHrsRackSnapshot(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, [proposalEditKey]);
  const returnPath = resolveHrsReturnPath({
    returnTo: navigationParams.get("returnTo"),
    catalogue: navigationParams.get("catalogue"),
  });
  const returnLabel = returnPath.startsWith("/sales/catalogue")
    ? "Sales Catalogue"
    : proposalEditKey
      ? `Back to ${proposalRef}`
      : "Sales Sandbox";
  const isNewProject = navigationParams.get("new") === "1";
  const { user, loading } = useAuth();
  const allowed = user?.role === "sales" || user?.role === "admin";
  const isHrsTester = isHrsTesterEmail(user?.email);
  const initialProposalConfiguration = proposalEditSource?.configuration;
  const [series, setSeries] = useState<HrsSeries>(
      initialProposalConfiguration?.series ?? "SXR"
    ),
    [sizeCode, setSizeCode] = useState(
      initialProposalConfiguration?.sizeCode ?? "1921"
    ),
    [finish, setFinish] = useState<HrsFinish>(
      initialProposalConfiguration?.finish ?? "black"
    ),
    [baseSeries, setBaseSeries] = useState<HrsBaseSeries>(
      initialProposalConfiguration?.baseSeries ?? "M3X2"
    );
  const [rackLevels, setRackLevels] = useState(
      initialProposalConfiguration?.rackLevels ?? [3]
    ),
    [slots, setSlots] = useState<EquipmentSlot[]>(() =>
      initialProposalConfiguration
        ? initialProposalConfiguration.slots.map(slot => ({
            ...slot,
            baseSeries:
              slot.baseSeries ?? initialProposalConfiguration.baseSeries,
            source: "manual" as const,
            confidence: null,
          }))
        : Array.from({ length: 3 }, (_, i) => emptySlot(`1-${i + 1}`))
    ),
    [lookupSlotKey, setLookupSlotKey] = useState<string | null>(null),
    [searchSlotKey, setSearchSlotKey] = useState<string | null>(null),
    [imageSlotKey, setImageSlotKey] = useState<string | null>(null),
    [isDownloading, setIsDownloading] = useState(false),
    [isPreviewExpanded, setIsPreviewExpanded] = useState(false),
    [selectedSlotKey, setSelectedSlotKey] = useState(
      initialProposalConfiguration?.slots[0]?.key ?? "1-1"
    ),
    [customBase, setCustomBase] = useState<CustomBase | null>(
      initialProposalConfiguration?.customBase ?? null
    ),
    [savedConfigurationId, setSavedConfigurationId] = useState<number | null>(
      () => proposalEditSource
        ? null
        : Number(new URLSearchParams(window.location.search).get("rackId")) ||
          null
    );
  const trpcUtils = trpc.useUtils();
  const savedQ = trpc.hrsRackConfigurator.listSaved.useQuery(undefined, {
    enabled: allowed && !isHrsTester,
  });
  const loadedRackQ = trpc.hrsRackConfigurator.getSaved.useQuery(
    { id: savedConfigurationId ?? 0 },
    {
      enabled:
        allowed &&
        !isHrsTester &&
        savedConfigurationId != null &&
        proposalEditKey == null,
    }
  );
  const saveRackMut = trpc.hrsRackConfigurator.save.useMutation();
  const duplicateRackMut = trpc.hrsRackConfigurator.duplicate.useMutation();
  const renameRackMut = trpc.hrsRackConfigurator.rename.useMutation();
  const deleteRackMut = trpc.hrsRackConfigurator.deleteSaved.useMutation();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const pdfDrawingRef = useRef<HTMLDivElement>(null);
  const matchingBases = HRS_BASE_OPTIONS.filter(
      option => option.sizeCode === sizeCode
    ),
    standardBase =
      series === "EXR"
        ? null
        : (findHrsBase(baseSeries, sizeCode) ?? matchingBases[0] ?? null);
  const customBaseOption = customBase
      ? {
          series: customBase.series,
          sizeCode: "CUSTOM",
          sku: `Custom ${customBase.series} ${customBase.widthIn}×${customBase.depthIn}`,
          widthIn: customBase.widthIn,
          depthIn: customBase.depthIn,
          msrpUsd:
            customBase.widthIn *
            customBase.depthIn *
            (customBase.series === "E1X" ? 6 : 13),
        }
      : null,
    base = series === "EXR" ? null : (customBaseOption ?? standardBase),
    nominalWidth = base?.widthIn ?? (Number(sizeCode.slice(2)) || 21),
    nominalDepth = base?.depthIn ?? (Number(sizeCode.slice(0, 2)) || 19),
    baseSku = base?.sku ?? `${series}-${sizeCode} plinth`;
  const basesBySlot = Object.fromEntries(
    slots.map(slot => {
      const selected =
        series === "EXR"
          ? null
          : customBaseOption ??
            findHrsBase(slot.baseSeries ?? baseSeries, sizeCode) ??
            standardBase;
      return [slot.key, selected] as const;
    })
  );
  const selectedBases =
    series === "EXR"
      ? []
      : slots
          .map(slot => basesBySlot[slot.key])
          .filter((selected): selected is NonNullable<typeof base> => Boolean(selected));
  const baseSkusBySlot = Object.fromEntries(
    slots.map(slot => [slot.key, basesBySlot[slot.key]?.sku ?? baseSku])
  );
  const solidBracePairCount =
    series === "SXR" ? slots.filter(slot => slot.solidBrace).length : 0;
  const extendedSpacingCounts = slots.reduce(
    (counts, slot) => {
      const [rack, level] = slot.key.split("-").map(Number);
      if (
        series === "SXR" &&
        rackLevels[rack - 1] !== level &&
        (slot.spacingIn === 12 ||
          slot.spacingIn === 14 ||
          slot.spacingIn === 16)
      ) {
        counts[slot.spacingIn] += 1;
      }
      return counts;
    },
    { 12: 0, 14: 0, 16: 0 }
  );
  const priceQ = trpc.hrsRackConfigurator.calculatePrice.useQuery(
    {
      series,
      sizeCode,
      rackLevels,
      base,
      bases: selectedBases,
      solidBracePairCount,
      extendedSpacingCounts,
    },
    { enabled: allowed }
  );
  useEffect(() => {
    if (!isNewProject) return;
    setSavedConfigurationId(null);
    setSeries("SXR");
    setSizeCode("1921");
    setFinish("black");
    setBaseSeries("M3X2");
    setRackLevels([3]);
    setSlots(Array.from({ length: 3 }, (_, i) => emptySlot(`1-${i + 1}`)));
    setSelectedSlotKey("1-1");
    setCustomBase(null);
  }, [isNewProject]);
  useEffect(() => {
    const loaded = proposalEditSource?.configuration;
    if (!loaded) return;
    setSavedConfigurationId(null);
    setSeries(loaded.series);
    setSizeCode(loaded.sizeCode);
    setFinish(loaded.finish);
    setBaseSeries(loaded.baseSeries);
    setRackLevels(loaded.rackLevels);
    setCustomBase(loaded.customBase);
    setSlots(
      loaded.slots.map(slot => ({
        ...slot,
        baseSeries: slot.baseSeries ?? loaded.baseSeries,
        source: "manual" as const,
        confidence: null,
      }))
    );
    setSelectedSlotKey(loaded.slots[0]?.key ?? "1-1");
  }, [proposalEditSource]);
  useEffect(() => {
    const loaded = loadedRackQ.data?.configuration;
    if (!loaded) return;
    setSeries(loaded.series);
    setSizeCode(loaded.sizeCode);
    setFinish(loaded.finish);
    setBaseSeries(loaded.baseSeries);
    setRackLevels(loaded.rackLevels);
    setCustomBase(loaded.customBase);
    setSlots(
      loaded.slots.map(slot => ({
        ...slot,
        baseSeries: slot.baseSeries ?? loaded.baseSeries,
        source: "manual" as const,
        confidence: null,
      }))
    );
    setSelectedSlotKey(loaded.slots[0]?.key ?? "1-1");
  }, [loadedRackQ.data]);
  const sizeOptions = Array.from(
    new Map(
      HRS_BASE_OPTIONS.map(option => [option.sizeCode, option] as const)
    ).values()
  ).filter(
    option => series !== "EXR" || ["1719", "1921"].includes(option.sizeCode)
  );
  const searchedSlot = slots.find(slot => slot.key === searchSlotKey),
    deferredSearch = useDeferredValue(searchedSlot?.name.trim() ?? ""),
    activeImageSlot = slots.find(slot => slot.key === imageSlotKey) ?? null;
  const requiredWidth = Math.round(
      Math.max(0, ...slots.map(slot => slot.widthIn ?? 0))
    ),
    requiredDepth = Math.round(
      Math.max(0, ...slots.map(slot => slot.depthIn ?? 0))
    ),
    hasKnownFootprint = requiredWidth > 1 || requiredDepth > 1,
    standardBaseFits = HRS_BASE_OPTIONS.some(
      option =>
        option.widthIn >= requiredWidth && option.depthIn >= requiredDepth
    ),
    needsCustomBase = hasKnownFootprint && !standardBaseFits;
  const libraryQ = trpc.projectCatalogueSandbox.list.useQuery(
    { search: deferredSearch, sort: "most_used", limit: 5, seedDefaults: true },
    { enabled: allowed && deferredSearch.length >= 2 }
  );
  const enrich =
    trpc.projectCatalogueSandbox.aiEnrichProductQuery.useMutation();
  const lookupInFlightRef = useRef(new Set<string>());
  useEffect(() => {
    const keys = rackLevels.flatMap((count, rackIndex) =>
      Array.from(
        { length: count },
        (_, levelIndex) => `${rackIndex + 1}-${levelIndex + 1}`
      )
    );
    setSlots(current => {
      const existing = new Map(current.map(slot => [slot.key, slot]));
      return keys.map(key => existing.get(key) ?? emptySlot(key));
    });
  }, [rackLevels.join(",")]);
  useEffect(() => {
    if (slots.some(slot => slot.key === selectedSlotKey)) return;
    setSelectedSlotKey(slots[0]?.key ?? "1-1");
  }, [selectedSlotKey, slots]);
  useEffect(() => {
    if (series === "EXR" && !["1719", "1921"].includes(sizeCode)) {
      setSizeCode("1921");
    }
  }, [series, sizeCode]);
  const updateSlot = (key: string, patch: Partial<EquipmentSlot>) =>
    setSlots(current =>
      current.map(slot => (slot.key === key ? { ...slot, ...patch } : slot))
    );
  const selectSlotFromDrawing = (key: string) => {
    setSelectedSlotKey(key);
    setIsPreviewExpanded(false);
  };
  const updateRackLevels = (rackIndex: number, count: number) =>
    setRackLevels(current =>
      current.map((value, index) => (index === rackIndex ? count : value))
    );
  const setRackCount = (count: number) =>
    setRackLevels(current =>
      Array.from(
        { length: count },
        (_, index) => current[index] ?? current[current.length - 1] ?? 3
      )
    );
  const activateCustomBase = (customSeries: CustomBase["series"]) => {
    const suggestedWidth =
      customSeries === "E1X"
        ? (E1X_CUSTOM_WIDTHS.find(width => width >= requiredWidth) ?? 25)
        : requiredWidth;
    setCustomBase({
      series: customSeries,
      widthIn: Math.max(suggestedWidth, customSeries === "E1X" ? 9.5 : 1),
      depthIn: Math.max(requiredDepth, 1),
    });
    setBaseSeries(customSeries);
  };
  const useLibraryItem = (
    key: string,
    item: NonNullable<typeof libraryQ.data>["items"][number]
  ) => {
    updateSlot(key, {
      name: [item.manufacturer, item.productName].filter(Boolean).join(" "),
      widthIn: toInches(item.width, item.dimensionUnit),
      depthIn: toInches(item.depth, item.dimensionUnit),
      heightIn: toInches(item.height, item.dimensionUnit),
      weightKg: toKg(item.weight, item.weightUnit),
      source: "library",
      confidence: "high",
    });
    setSearchSlotKey(null);
  };
  const lookupEquipment = async (slot: EquipmentSlot) => {
    if (!slot.name.trim() || lookupInFlightRef.current.has(slot.key)) return;
    const normalizedQuery = normalizeEquipmentSearch(slot.name);
    const libraryMatch = libraryQ.data?.items.find(item => {
      const candidate = normalizeEquipmentSearch(
        [item.manufacturer, item.productName].filter(Boolean).join(" ")
      );
      return candidate === normalizedQuery;
    });
    if (libraryMatch) {
      const knownDimensions = [
        libraryMatch.width,
        libraryMatch.depth,
        libraryMatch.height,
      ].filter(value => value != null && value !== "").length;
      if (knownDimensions === 3) {
        useLibraryItem(slot.key, libraryMatch);
        toast.success("Specifications found in the equipment library and added.");
        return;
      }
    }
    lookupInFlightRef.current.add(slot.key);
    const toastId = `hrs-spec-lookup-${slot.key}`;
    setLookupSlotKey(slot.key);
    try {
      const data = await enrich.mutateAsync({ query: slot.name.trim() });
      const found = {
        widthIn: toInches(data.result.width, data.result.dimensionUnit),
        depthIn: toInches(data.result.depth, data.result.dimensionUnit),
        heightIn: toInches(data.result.height, data.result.dimensionUnit),
        weightKg: toKg(data.result.weight, data.result.weightUnit),
      };
      const lookupStatus = classifyHrsEquipmentLookup(found);
      if (lookupStatus !== "empty") {
        updateSlot(slot.key, {
          ...mergeHrsEquipmentDimensions(slot, found),
          source: "web",
          confidence: data.result.confidence,
        });
      }
      if (lookupStatus === "complete") {
        toast.success("Specifications found and added. Please review them.", {
          id: toastId,
        });
      } else if (lookupStatus === "partial") {
        toast.warning(
          "Partial specifications found and added. Please fill in the missing fields.",
          { id: toastId }
        );
      } else {
        toast.warning(
          "No usable dimensions were found. Try adding the brand, or enter them manually.",
          { id: toastId }
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `Specification search failed: ${error.message}`
          : "Specification search failed. Please try again.",
        { id: toastId }
      );
    } finally {
      lookupInFlightRef.current.delete(slot.key);
      setLookupSlotKey(null);
    }
  };
  const uploadEquipmentImage = (file: File | undefined) => {
    if (!file || !imageSlotKey) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateSlot(imageSlotKey, { imageUrl: String(reader.result) });
      toast.success("Equipment image added.");
    };
    reader.readAsDataURL(file);
  };
  const slotFits = useMemo(
    () =>
      slots.map(slot => {
        const [rackNumber, levelNumber] = slot.key.split("-").map(Number);
        const isTopLevel = rackLevels[rackNumber - 1] === levelNumber;
        const braceAbove =
          series === "SXR" &&
          slots.find(
            candidate => candidate.key === `${rackNumber}-${levelNumber + 1}`
          )?.solidBrace;
        const usableHeightIn = braceAbove
          ? Math.max(0, slot.spacingIn - HRS_SXR_SOLID_BRACE_CLEARANCE_IN)
          : slot.spacingIn;
        return {
          slot,
          fit: checkHrsEquipmentFit({
            equipmentName: slot.name,
            dimensions: slot,
            base: basesBySlot[slot.key] ?? { widthIn: nominalWidth, depthIn: nominalDepth },
            clearHeightIn: isTopLevel
              ? Number.POSITIVE_INFINITY
              : usableHeightIn,
            ventilationAllowanceIn: isTopLevel ? 0 : 2,
          }),
        };
      }),
    [slots, basesBySlot, nominalWidth, nominalDepth, rackLevels]
  );
  const price = priceQ.data ?? {
      customQuoteRequired: false,
      configuredPriceSgd: null,
      configuredBasePriceSgd: null,
    },
    totalLevels = rackLevels.reduce((sum, value) => sum + value, 0),
    issueCount = slotFits.filter(
      ({ fit }) => fit.status === "does_not_fit"
    ).length,
    configurationCode =
      rackLevels.length === 1 ? `${rackLevels[0]}V` : rackLevels.join("T");
  const configurationPayload = () => ({
    series,
    sizeCode,
    finish,
    baseSeries,
    rackLevels,
    customBase,
    slots: slots.map(
      ({
        key,
        baseSeries: slotBaseSeries,
        name,
        spacingIn,
        widthIn,
        depthIn,
        heightIn,
        weightKg,
        imageUrl,
        solidBrace,
      }) => ({
        key,
        baseSeries: slotBaseSeries ?? baseSeries,
        name,
        spacingIn,
        widthIn,
        depthIn,
        heightIn,
        weightKg,
        imageUrl,
        solidBrace,
      })
    ),
  });
  const saveRack = async (addToProposal = false) => {
    const existingCustomer =
      proposalEditSource?.customerName ?? loadedRackQ.data?.customerName ?? "";
    const customerName = proposalEditKey
      ? existingCustomer.trim()
      : window
          .prompt("Customer name for this rack configuration", existingCustomer)
          ?.trim();
    if (!customerName) return;
    try {
      const snapshot = await saveRackMut.mutateAsync({
        id: savedConfigurationId ?? undefined,
        customerName,
        configuration: configurationPayload(),
      });
      setSavedConfigurationId(snapshot.savedConfigurationId);
      const savedUrl = new URL(window.location.href);
      savedUrl.searchParams.delete("new");
      savedUrl.searchParams.set(
        "rackId",
        String(snapshot.savedConfigurationId)
      );
      window.history.replaceState(null, "", savedUrl.toString());
      await trpcUtils.hrsRackConfigurator.listSaved.invalidate();
      if (proposalEditKey) {
        localStorage.removeItem(
          `hrs-rack-proposal-edit-source:${proposalEditKey}`
        );
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: "hrs-rack-revision-updated",
              proposalEditKey,
              snapshot,
            },
            window.location.origin
          );
          window.close();
          return;
        }
        sessionStorage.setItem(
          "hrs-rack-revision-updated",
          JSON.stringify({ proposalEditKey, snapshot })
        );
        window.location.assign(returnPath);
        return;
      }
      if (addToProposal) {
        sessionStorage.setItem(
          "hrs-rack-add-to-proposal",
          JSON.stringify(snapshot)
        );
        window.location.assign(
          returnPath.startsWith("/sales/catalogue")
            ? `${HRS_CATALOGUE_PATH}&hrsConfigured=1`
            : returnPath
        );
      } else {
        toast.success(`Rack configuration saved for ${customerName}.`);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not save rack."
      );
    }
  };
  const duplicateRack = async (id: number) => {
    const customerName = window
      .prompt("Customer name for the duplicated rack")
      ?.trim();
    if (!customerName) return;
    try {
      const result = await duplicateRackMut.mutateAsync({ id, customerName });
      await trpcUtils.hrsRackConfigurator.listSaved.invalidate();
      window.location.assign(
        buildHrsConfiguratorPath({ rackId: result.id, returnTo: returnPath })
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not duplicate rack."
      );
    }
  };
  const renameRack = async (id: number, currentName: string) => {
    const customerName = window.prompt("Project / customer name", currentName)?.trim();
    if (!customerName || customerName === currentName) return;
    try {
      await renameRackMut.mutateAsync({ id, customerName });
      await Promise.all([
        trpcUtils.hrsRackConfigurator.listSaved.invalidate(),
        trpcUtils.hrsRackConfigurator.getSaved.invalidate(),
      ]);
      toast.success("Project name updated.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not rename project.");
    }
  };
  const deleteRack = async (id: number, currentName: string) => {
    if (!window.confirm(`Delete the saved rack for ${currentName}? This cannot be undone.`)) return;
    try {
      await deleteRackMut.mutateAsync({ id });
      await trpcUtils.hrsRackConfigurator.listSaved.invalidate();
      if (savedConfigurationId === id) {
        window.location.assign(
          buildHrsConfiguratorPath({ returnTo: returnPath, newProject: true })
        );
        return;
      }
      toast.success("Saved rack deleted.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not delete saved rack.");
    }
  };
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadHrsRackConfigurationPdf({
        series,
        finish,
        configurationCode: `${sizeCode}-${configurationCode}`,
        rackLevels,
        shelfSku:
          new Set(Object.values(baseSkusBySlot)).size > 1
            ? "Mixed isolation bases by level"
            : baseSku,
        shelfWidthIn: nominalWidth,
        shelfDepthIn: nominalDepth,
        shelfQuantity: totalLevels,
        solidBracePairCount,
        configuredPrice: isHrsTester
          ? null
          : price.configuredPriceSgd == null
            ? "Custom quote required"
            : money(price.configuredPriceSgd),
        drawingElement: pdfDrawingRef.current,
        slots: slotFits.map(({ slot, fit }) => {
          const [rack, level] = slot.key.split("-").map(Number);
          return {
            rack,
            level,
            baseSku: baseSkusBySlot[slot.key] ?? baseSku,
            openTop: rackLevels[rack - 1] === level,
            ...slot,
            usableHeightIn:
              rackLevels[rack - 1] === level
                ? null
                : Math.max(
                    0,
                    slot.spacingIn -
                      (series === "SXR" &&
                      slots.find(
                        candidate => candidate.key === `${rack}-${level + 1}`
                      )?.solidBrace
                        ? HRS_SXR_SOLID_BRACE_CLEARANCE_IN
                        : 0)
                  ),
            fit,
          };
        }),
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "PDF download failed."
      );
    } finally {
      setIsDownloading(false);
    }
  };
  if (loading)
    return (
      <DashboardLayout>
        <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
          Loading…
        </div>
      </DashboardLayout>
    );
  if (!user) return <Redirect to="/login" />;
  if (!allowed) return <Redirect to="/sales/sandbox" />;
  if (proposalEditKey && !proposalEditSource)
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-xl py-12">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>The original rack configuration could not be loaded</AlertTitle>
            <AlertDescription>
              No changes have been made. Return to {proposalRef} and select Edit
              configuration again so the complete rack details can be transferred.
            </AlertDescription>
          </Alert>
          <Button
            className="mt-4 bg-[#1A2744] text-white hover:bg-[#243556]"
            onClick={() => {
              if (window.opener && !window.opener.closed) {
                window.close();
                return;
              }
              window.location.assign(returnPath);
            }}
          >
            Back to {proposalRef}
          </Button>
        </div>
      </DashboardLayout>
    );
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-7xl space-y-5 pb-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            {!isHrsTester && (
              proposalEditKey ? (
                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem(
                      `hrs-rack-proposal-edit-source:${proposalEditKey}`
                    );
                    if (window.opener && !window.opener.closed) {
                      window.close();
                      return;
                    }
                    window.location.assign(returnPath);
                  }}
                  className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" /> {returnLabel}
                </button>
              ) : (
                <Link
                  href={returnPath}
                  className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" /> {returnLabel}
                </Link>
              )
            )}
            {proposalEditKey ? (
              <div className="mt-3 flex w-fit rounded-md border border-[#1A2744] bg-[#1A2744] px-3 py-1.5 text-sm font-semibold uppercase tracking-wide text-white">
                Editing {proposalRef}, {proposalEditSource?.customerName ?? "Customer"}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="border-amber-300 bg-amber-50 text-amber-800"
                >
                  Sandbox
                </Badge>
                <span className="text-xs text-muted-foreground">
                  March 2026 dealer price list
                </span>
              </div>
            )}
            <h1 className={proposalEditKey ? "mt-5 text-2xl font-semibold tracking-tight" : "mt-2 text-2xl font-semibold tracking-tight"}>
              HRS Rack Configurator
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure one joined rack system. Equipment remains optional.
            </p>
          </div>
          {!isHrsTester && (
            <div className="flex w-full flex-col items-end gap-2 sm:w-[245px]">
              <Card className="w-full border-amber-200 bg-amber-50/50">
                <CardContent className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-amber-800">
                    Configured price
                  </p>
                  {price.configuredPriceSgd == null ? (
                    <p className="mt-1 text-xl font-semibold">
                      Custom quote required
                    </p>
                  ) : (
                    <p className="mt-1 text-3xl font-semibold tracking-tight">
                      {money(price.configuredPriceSgd)}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    SGD · GST included
                    {customBase ? " · includes estimated custom bases" : ""}
                  </p>
                </CardContent>
              </Card>
              <div className={proposalEditKey ? "flex w-full gap-2" : "flex gap-2"}>
                <Button
                  variant={proposalEditKey ? "default" : "outline"}
                  className={proposalEditKey ? "w-full bg-[#1A2744] text-white hover:bg-[#243556]" : undefined}
                  onClick={() => void saveRack(false)}
                  disabled={saveRackMut.isPending}
                >
                  {proposalEditKey ? "Save changes and return" : "Save rack"}
                </Button>
                {!proposalEditKey && <Button
                  onClick={() => void saveRack(true)}
                  disabled={
                    saveRackMut.isPending || price.configuredPriceSgd == null
                  }
                >
                  Add to proposal
                </Button>}
              </div>
            </div>
          )}
        </div>
        {!isHrsTester && !proposalEditKey && (savedQ.data?.length ?? 0) > 0 && (
          <Card id="saved-racks">
            <CardHeader>
              <CardTitle className="text-base">Saved racks</CardTitle>
              <CardDescription>
                Open any draft to continue editing, or duplicate a rack already
                used in a proposal.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5 md:grid-cols-2">
              {(["draft", "in_proposal"] as const).map(status => (
                <section key={status} className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {status === "draft" ? "Drafts" : "In Proposals"}
                  </h3>
                  {savedQ.data?.filter(row => row.status === status).length ? (
                    savedQ.data
                      .filter(row => row.status === status)
                      .map(row => (
                        <div
                          key={row.id}
                          className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">
                              {row.customerName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {row.configurationName}
                            </p>
                          </div>
                          <div className="flex shrink-0 gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={renameRackMut.isPending}
                              onClick={() => void renameRack(row.id, row.customerName)}
                            >
                              Edit name
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                window.location.assign(
                                  buildHrsConfiguratorPath({
                                    rackId: row.id,
                                    returnTo: returnPath,
                                  })
                                )
                              }
                            >
                              Configure
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void duplicateRack(row.id)}
                            >
                              Duplicate
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-destructive hover:text-destructive"
                              title="Delete saved rack"
                              disabled={deleteRackMut.isPending}
                              onClick={() => void deleteRack(row.id, row.customerName)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                  ) : (
                    <p className="text-sm text-muted-foreground">None yet.</p>
                  )}
                </section>
              ))}
            </CardContent>
          </Card>
        )}
        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_430px]">
          <div className="contents">
            <Card className="order-1 xl:col-start-1 xl:row-start-1">
              <CardHeader>
                <CardTitle className="text-base">
                  1. Rack configuration
                </CardTitle>
                <CardDescription>
                  All joined racks use the same series, footprint, finish, and
                  shelf type.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label>Series</Label>
                  <Select
                    value={series}
                    onValueChange={value => setSeries(value as HrsSeries)}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(["EXR", "SXR", "VXR"] as HrsSeries[]).map(value => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Frame size</Label>
                  <Select value={sizeCode} onValueChange={setSizeCode}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sizeOptions.map(option => (
                        <SelectItem
                          key={option.sizeCode}
                          value={option.sizeCode}
                        >
                          {option.sizeCode} · {option.widthIn}″ W ×{" "}
                          {option.depthIn}″ D
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Finish</Label>
                  <Select
                    value={finish}
                    onValueChange={value => setFinish(value as HrsFinish)}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="black">Black</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Racks wide</Label>
                  <Select
                    value={String(rackLevels.length)}
                    onValueChange={value => setRackCount(Number(value))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4].map(value => (
                        <SelectItem key={value} value={String(value)}>
                          {value}-wide
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {rackLevels.map((count, index) => (
                  <div key={index}>
                    <Label>Rack {index + 1} levels</Label>
                    <Select
                      value={String(count)}
                      onValueChange={value =>
                        updateRackLevels(index, Number(value))
                      }
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map(value => (
                          <SelectItem key={value} value={String(value)}>
                            {value} level{value > 1 ? "s" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {series !== "EXR" && (
                  <div>
                    <Label>Default isolation base</Label>
                    <Select
                      value={base?.sku ?? ""}
                      onValueChange={value => {
                        const option = HRS_BASE_OPTIONS.find(
                          candidate => candidate.sku === value
                        );
                        if (!option) return;
                        setCustomBase(null);
                        setBaseSeries(option.series);
                        setSlots(current =>
                          current.map(slot => ({ ...slot, baseSeries: option.series }))
                        );
                      }}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(["E1X", "R3X", "S3", "M3X2"] as HrsBaseSeries[]).map(
                          group => (
                            <SelectGroup key={group}>
                              <SelectLabel>{group}</SelectLabel>
                              {HRS_BASE_OPTIONS.filter(
                                option => option.series === group && option.sizeCode === sizeCode
                              ).map(option => (
                                <SelectItem key={option.sku} value={option.sku}>
                                  {option.sizeCode} · {option.widthIn}″ W ×{" "}
                                  {option.depthIn}″ D
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )
                        )}
                        {customBase && (
                          <SelectGroup>
                            <SelectLabel>Custom</SelectLabel>
                            <SelectItem value={customBaseOption!.sku}>
                              {customBaseOption!.sku}
                            </SelectItem>
                          </SelectGroup>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Applied to every level initially. Each shelf can be changed in Step 2; all bases remain {sizeCode}.
                    </p>
                    {customBase && (
                      <div className="mt-3 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 sm:col-span-2">
                        <p className="text-xs font-medium text-amber-900">
                          Custom {customBase.series} size for every level
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Width (in)</Label>
                            {customBase.series === "E1X" ? (
                              <Select
                                value={String(customBase.widthIn)}
                                onValueChange={value =>
                                  setCustomBase(current =>
                                    current
                                      ? { ...current, widthIn: Number(value) }
                                      : current
                                  )
                                }
                              >
                                <SelectTrigger className="mt-1 w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {E1X_CUSTOM_WIDTHS.map(width => (
                                    <SelectItem
                                      key={width}
                                      value={String(width)}
                                    >
                                      {width}″
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                className="mt-1"
                                inputMode="decimal"
                                value={customBase.widthIn}
                                onChange={event =>
                                  setCustomBase(current =>
                                    current
                                      ? {
                                          ...current,
                                          widthIn:
                                            numberOrNull(event.target.value) ??
                                            1,
                                        }
                                      : current
                                  )
                                }
                              />
                            )}
                          </div>
                          <div>
                            <Label className="text-xs">Depth (in)</Label>
                            <Input
                              className="mt-1"
                              inputMode="decimal"
                              value={customBase.depthIn}
                              onChange={event =>
                                setCustomBase(current =>
                                  current
                                    ? {
                                        ...current,
                                        depthIn:
                                          numberOrNull(event.target.value) ?? 1,
                                      }
                                    : current
                                )
                              }
                            />
                          </div>
                        </div>
                        <p className="text-xs text-amber-900">
                          Approx. base MSRP: US$
                          {Math.round(
                            customBaseOption!.msrpUsd
                          ).toLocaleString()}{" "}
                          each
                          {!isHrsTester && (
                            <>
                              {" "}
                              · configured base price:{" "}
                              {price.configuredBasePriceSgd == null
                                ? "Calculating…"
                                : money(price.configuredBasePriceSgd)}
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="order-2 overflow-hidden xl:col-start-1 xl:row-start-2">
              <CardHeader className="border-b bg-muted/30">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">
                      Joined rack drawing
                    </CardTitle>
                    <CardDescription>
                      Select a shelf to configure it. The selected shelf is
                      highlighted.
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">
                      {rackLevels.length}-wide · {configurationCode}
                    </Badge>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setIsPreviewExpanded(true)}
                    >
                      <Maximize2 className="mr-2 h-4 w-4" />
                      Expand
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleDownload}
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      Download rack configuration
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="h-[min(58vh,640px)] min-h-[360px] overflow-x-auto bg-[#f8f8f5] p-4">
                <div
                  className="mx-auto h-full [&>svg]:h-full [&>svg]:w-full"
                  style={{
                    minWidth: `${Math.max(360, rackLevels.length * 230)}px`,
                  }}
                >
                  <JoinedRackElevation
                    series={series}
                    finish={finish}
                    rackLevels={rackLevels}
                    slots={slots}
                    baseSku={baseSku}
                    baseSkusBySlot={baseSkusBySlot}
                    baseWidth={nominalWidth}
                    baseDepth={nominalDepth}
                    selectedSlotKey={selectedSlotKey}
                    onSelectSlot={selectSlotFromDrawing}
                  />
                </div>
              </CardContent>
            </Card>
            <Dialog
              open={isPreviewExpanded}
              onOpenChange={setIsPreviewExpanded}
            >
              <DialogContent className="max-h-[95vh] max-w-[95vw] overflow-auto sm:max-w-[95vw]">
                <DialogHeader>
                  <DialogTitle>Joined rack drawing</DialogTitle>
                  <DialogDescription>
                    Select a shelf to configure it. The selected shelf is
                    highlighted.
                  </DialogDescription>
                </DialogHeader>
                <div className="mx-auto w-full max-w-6xl bg-[#f8f8f5] p-5">
                  <JoinedRackElevation
                    series={series}
                    finish={finish}
                    rackLevels={rackLevels}
                    slots={slots}
                    baseSku={baseSku}
                    baseSkusBySlot={baseSkusBySlot}
                    baseWidth={nominalWidth}
                    baseDepth={nominalDepth}
                    selectedSlotKey={selectedSlotKey}
                    onSelectSlot={selectSlotFromDrawing}
                  />
                </div>
              </DialogContent>
            </Dialog>
            <div
              ref={pdfDrawingRef}
              aria-hidden="true"
              className="pointer-events-none fixed -left-[12000px] top-0 bg-[#f8f8f5] p-5"
              style={{ width: `${Math.max(720, rackLevels.length * 300)}px` }}
            >
              <JoinedRackElevation
                series={series}
                finish={finish}
                rackLevels={rackLevels}
                slots={slots}
                baseSku={baseSku}
                baseSkusBySlot={baseSkusBySlot}
                baseWidth={nominalWidth}
                baseDepth={nominalDepth}
                selectedSlotKey=""
                onSelectSlot={() => undefined}
              />
            </div>
            {needsCustomBase && series !== "EXR" && !customBase && (
              <Alert className="order-5 border-amber-300 bg-amber-50 xl:col-start-1 xl:row-start-3">
                <AlertTriangle className="h-4 w-4 text-amber-700" />
                <AlertTitle>
                  Standard-size bases are not suitable for this equipment
                </AlertTitle>
                <AlertDescription>
                  <p>
                    Create one custom size for every level. The suggested size
                    includes 1″ clearance and can be adjusted.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {requiredWidth <= 25 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => activateCustomBase("E1X")}
                      >
                        Customize E1X
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => activateCustomBase("M3X2")}
                    >
                      Customize M3X2
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            <Card className="order-4 xl:col-start-2 xl:row-start-2">
              <CardHeader>
                <CardTitle className="text-base">
                  2. Configure selected shelf
                </CardTitle>
                <CardDescription>
                  Click another shelf in the drawing to switch locations.
                  Equipment remains optional.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {rackLevels.map((count, rackIndex) =>
                  selectedSlotKey.startsWith(`${rackIndex + 1}-`) ? (
                    <div key={rackIndex} className="space-y-3">
                      <div className="flex items-center gap-3 pt-1">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-sm font-semibold">
                          Rack {rackIndex + 1}
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      {Array.from({ length: count }, (_, levelIndex) => {
                        const key = `${rackIndex + 1}-${levelIndex + 1}`,
                          slot =
                            slots.find(candidate => candidate.key === key) ??
                            emptySlot(key),
                          frame = findHrsFrame(series, sizeCode, count),
                          isTopLevel = levelIndex === count - 1,
                          fit =
                            slotFits.find(entry => entry.slot.key === key)
                              ?.fit ??
                            checkHrsEquipmentFit({
                              dimensions: slot,
                              base,
                              clearHeightIn: isTopLevel
                                ? Number.POSITIVE_INFINITY
                                : slot.spacingIn,
                              ventilationAllowanceIn: isTopLevel ? 0 : 2,
                            });
                        const fitLabel =
                          fit.status === "fits"
                            ? "Fit checked — suitable"
                            : fit.status === "does_not_fit"
                              ? "Fit checked — issue found"
                              : fit.status === "review"
                                ? "Specifications incomplete"
                                : "Not checked — equipment not specified";
                        if (key !== selectedSlotKey) return null;
                        return (
                          <div
                            id={`hrs-slot-${key}`}
                            key={key}
                            className={`animate-in scroll-mt-6 rounded-lg border p-4 transition-shadow duration-200 slide-in-from-top-2 ${
                              selectedSlotKey === key
                                ? "border-amber-400 ring-2 ring-amber-200"
                                : ""
                            }`}
                          >
                            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                              <h3 className="font-semibold">
                                Rack {rackIndex + 1}: Level {levelIndex + 1}
                              </h3>
                              {isTopLevel ? (
                                <Badge variant="outline">
                                  Open top · no height limit
                                </Badge>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Label className="whitespace-nowrap">
                                    Opening height
                                  </Label>
                                  <Select
                                    value={String(slot.spacingIn)}
                                    onValueChange={value =>
                                      updateSlot(key, {
                                        spacingIn: Number(value),
                                      })
                                    }
                                  >
                                    <SelectTrigger className="w-24">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(
                                        frame?.standardSpacing ??
                                        (series === "SXR"
                                          ? [2, 4, 6, 8, 10]
                                          : series === "EXR"
                                            ? [4, 6, 8, 10, 12]
                                            : [8])
                                      ).map(value => (
                                        <SelectItem
                                          key={value}
                                          value={String(value)}
                                        >
                                          {value}″
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                            {series !== "EXR" && !customBase && (
                              <div className="mb-4 rounded-md border bg-slate-50 p-3">
                                <Label>Isolation base for this level</Label>
                                <Select
                                  value={slot.baseSeries ?? baseSeries}
                                  onValueChange={value =>
                                    updateSlot(key, { baseSeries: value as HrsBaseSeries })
                                  }
                                >
                                  <SelectTrigger className="mt-1.5 w-full sm:w-72">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {HRS_BASE_OPTIONS.filter(option => option.sizeCode === sizeCode).map(option => (
                                      <SelectItem key={option.sku} value={option.series}>
                                        {option.sku} · {option.widthIn}″ W × {option.depthIn}″ D
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  This changes the base grade only. The {sizeCode} footprint is locked for the whole rack.
                                </p>
                              </div>
                            )}
                            {series === "SXR" && (
                              <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/60 p-3">
                                <Checkbox
                                  id={`solid-brace-${key}`}
                                  checked={slot.solidBrace}
                                  onCheckedChange={checked =>
                                    updateSlot(key, {
                                      solidBrace: checked === true,
                                    })
                                  }
                                />
                                <div className="space-y-1">
                                  <Label
                                    htmlFor={`solid-brace-${key}`}
                                    className="cursor-pointer"
                                  >
                                    Add SXR Solid Brace Insert (1 pair)
                                  </Label>
                                  <p className="text-xs text-muted-foreground">
                                    Installed below this shelf. It reduces the
                                    usable opening beneath it by 0.38″.
                                  </p>
                                </div>
                              </div>
                            )}
                            <div className="relative">
                              <Label>Equipment brand and model</Label>
                              <div className="mt-1.5 flex gap-2">
                                <div className="relative flex-1">
                                  <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    value={slot.name}
                                    onFocus={() => setSearchSlotKey(key)}
                                    onChange={event => {
                                      updateSlot(key, {
                                        name: event.target.value,
                                        source: event.target.value
                                          ? "manual"
                                          : "none",
                                        confidence: null,
                                      });
                                      setSearchSlotKey(key);
                                    }}
                                    placeholder="Type any product, or leave blank"
                                    className="pl-9"
                                    onKeyDown={event => {
                                      if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
                                      event.preventDefault();
                                      void lookupEquipment(slot);
                                    }}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={
                                    !slot.name.trim() || lookupSlotKey === key
                                  }
                                  onClick={() => lookupEquipment(slot)}
                                >
                                  {lookupSlotKey === key ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="mr-2 h-4 w-4" />
                                  )}
                                  Find specs
                                </Button>
                              </div>
                              {searchSlotKey === key &&
                                slot.source === "manual" &&
                                (libraryQ.data?.items.length ?? 0) > 0 && (
                                  <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover p-1 shadow-lg">
                                    {libraryQ.data!.items.map(item => (
                                      <button
                                        type="button"
                                        key={item.id}
                                        onClick={() =>
                                          useLibraryItem(key, item)
                                        }
                                        className="flex w-full justify-between rounded px-3 py-2 text-left text-sm hover:bg-accent"
                                      >
                                        <span>
                                          {item.manufacturer} {item.productName}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                          Saved library
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                            </div>
                            <div className="mt-4 grid gap-3 sm:grid-cols-4">
                              {(
                                [
                                  "widthIn",
                                  "depthIn",
                                  "heightIn",
                                  "weightKg",
                                ] as const
                              ).map(field => (
                                <div key={field}>
                                  <Label>
                                    {field === "widthIn"
                                      ? "Width (in)"
                                      : field === "depthIn"
                                        ? "Depth (in)"
                                        : field === "heightIn"
                                          ? "Height (in)"
                                          : "Weight (kg)"}
                                  </Label>
                                  <Input
                                    className="mt-1.5"
                                    inputMode="decimal"
                                    value={
                                      slot[field] == null
                                        ? ""
                                        : String(Math.round(slot[field]))
                                    }
                                    onChange={event =>
                                      updateSlot(key, {
                                        [field]: numberOrNull(
                                          event.target.value
                                        ),
                                        source: "manual",
                                      })
                                    }
                                    placeholder="—"
                                  />
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={!slot.name.trim()}
                                onClick={() => setImageSlotKey(key)}
                              >
                                <ImagePlus className="mr-2 h-4 w-4" />
                                Find image
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setImageSlotKey(key);
                                  window.setTimeout(
                                    () => uploadInputRef.current?.click(),
                                    0
                                  );
                                }}
                              >
                                Upload image
                              </Button>
                              {slot.imageUrl && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    updateSlot(key, { imageUrl: null })
                                  }
                                >
                                  Remove image
                                </Button>
                              )}
                            </div>
                            <div
                              className={`mt-4 rounded-md border px-3 py-2 text-sm ${fitClasses(fit.status)}`}
                            >
                              <div className="flex items-center gap-2 font-medium">
                                <FitIcon status={fit.status} />
                                {fitLabel}
                              </div>
                              {fit.issues.map(issue => (
                                <p key={issue} className="mt-1 text-xs">
                                  {issue}
                                </p>
                              ))}
                              {fit.notes.map(note => (
                                <p key={note} className="mt-1 text-xs">
                                  {note}
                                </p>
                              ))}
                              {fit.status === "does_not_fit" && (
                                <p className="mt-2 text-xs font-medium">
                                  {fitRecommendation(slot, baseSkusBySlot[key] ?? baseSku)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null
                )}
              </CardContent>
            </Card>
            <Alert className="order-5 xl:col-start-2 xl:row-start-3">
              <Ruler className="h-4 w-4" />
              <AlertTitle>Nominal fit checks</AlertTitle>
              <AlertDescription>
                Width and depth are checked on every level. Enclosed levels also
                check height with a 2″ ventilation allowance; the open top has
                no height limit. Weight remains advisory until HRS load-range
                data is added.
              </AlertDescription>
            </Alert>
          </div>
          <div className="contents">
            <Card className="order-3 xl:col-start-2 xl:row-start-1">
              <CardHeader>
                <CardTitle className="text-base">
                  Configuration summary
                </CardTitle>
                <CardDescription>
                  One joined system; shelves are counted across every rack.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <span>
                    {series}-{sizeCode}-{configurationCode}
                  </span>
                  <span className="font-medium">
                    {rackLevels.length} rack{rackLevels.length > 1 ? "s" : ""}
                  </span>
                </div>
                {series !== "EXR" &&
                  Array.from(
                    selectedBases.reduce((counts, selected) => {
                      counts.set(selected.sku, (counts.get(selected.sku) ?? 0) + 1);
                      return counts;
                    }, new Map<string, number>())
                  ).map(([sku, quantity]) => (
                    <div key={sku} className="flex justify-between gap-4">
                      <span>{sku}</span>
                      <span className="font-medium">× {quantity}</span>
                    </div>
                  ))}
                {series === "SXR" && solidBracePairCount > 0 && (
                  <div className="flex justify-between gap-4">
                    <span>SXR Solid Brace Insert (1 pair)</span>
                    <span className="font-medium">× {solidBracePairCount}</span>
                  </div>
                )}
                {series === "SXR" &&
                  ([12, 14, 16] as const).map(spacing =>
                    extendedSpacingCounts[spacing] > 0 ? (
                      <div className="flex justify-between gap-4" key={spacing}>
                        <span>
                          SXR {spacing}&quot; extended spacing (6 posts)
                        </span>
                        <span className="font-medium">
                          × {extendedSpacingCounts[spacing]}
                        </span>
                      </div>
                    ) : null
                  )}
                {customBase && (
                  <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                    Custom {customBase.series} estimate uses US$
                    {customBase.series === "E1X" ? 6 : 13} per square inch.
                    Final dimensions and price require HRS confirmation.
                  </p>
                )}
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Equipment fit</span>
                    <span>
                      {issueCount
                        ? `${issueCount} issue${issueCount > 1 ? "s" : ""}`
                        : "No known issues"}
                    </span>
                  </div>
                  {!isHrsTester && (
                    <div className="mt-1 flex justify-between">
                      <span className="text-muted-foreground">
                        Configured price
                      </span>
                      <span className="font-semibold">
                        {price.configuredPriceSgd == null
                          ? "Custom quote"
                          : money(price.configuredPriceSgd)}
                      </span>
                    </div>
                  )}
                </div>
                {price.customQuoteRequired && (
                  <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                    This arrangement is not explicitly priced in the March 2026
                    list and requires an HRS quote.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={event => {
            uploadEquipmentImage(event.target.files?.[0]);
            event.currentTarget.value = "";
          }}
        />
        <ProductFindImageWorkflow
          open={Boolean(activeImageSlot)}
          onOpenChange={open => {
            if (!open) setImageSlotKey(null);
          }}
          searchContext={{
            productName: activeImageSlot?.name ?? "",
            displayName: activeImageSlot?.name ?? "",
          }}
          existingImageUrl={activeImageSlot?.imageUrl}
          forcedQuery={activeImageSlot?.name}
          identityLabel={activeImageSlot?.name || "Equipment"}
          onAccept={imageUrl => {
            if (imageSlotKey) updateSlot(imageSlotKey, { imageUrl });
            setImageSlotKey(null);
          }}
        />
      </div>
    </DashboardLayout>
  );
}
